import { Fragment, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useUiStore } from '@/store/uiStore'
import { useValuationStore } from '@/store/valuationStore'
import { useFieldHelp } from '@/hooks/useFieldHelp'
import { FIELD_LABEL_MAP } from '@/data/allSections'
import { parseHelpTextBlocks } from '@/utils/helpTextFormatter'
import { renderInlineMarkup } from '@/utils/renderInlineMarkup'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

export function HelpPanel() {
  const { activeHelpField, activeHelpSectionId, closeHelp } = useUiStore()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const isOpen = !!activeHelpField
  // Read live so the panel refreshes if the user changes the dropdown value while it's open.
  const currentValue = useValuationStore((s) =>
    activeHelpField && activeHelpSectionId ? s.formValues[activeHelpSectionId]?.[activeHelpField] : undefined
  )
  const { loading, hasHelp, helpText, helpImages, dropdownValueCode } = useFieldHelp(activeHelpField, currentValue)
  const fieldLabel = (activeHelpField && FIELD_LABEL_MAP[activeHelpField]) || getMessage('help_panel.default_title')
  // Show the field name as soon as we know which field is open, even before its content
  // has resolved — only fall back to the generic title once we know there's nothing to show.
  const title = hasHelp || loading ? fieldLabel : getMessage('help_panel.default_title')
  const blocks = useMemo(() => (helpText ? parseHelpTextBlocks(helpText) : []), [helpText])

  // If the user changes the field's value (e.g. to "Select" / "Not in List") while the panel
  // is open and the new value has no configured help, close the panel instead of falling back
  // to the generic "Field Guide" placeholder — an open panel should never show unconfigured content.
  useEffect(() => {
    if (activeHelpField && !loading && !hasHelp) {
      closeHelp()
    }
  }, [activeHelpField, loading, hasHelp, closeHelp])

  return (
    <aside
      className={cn(
        'fixed top-[60px] right-0 z-[250]',
        'flex flex-col bg-[var(--surface)] border-l border-[var(--border)]',
        'shadow-[-4px_0_24px_rgba(0,0,0,0.07)]',
        'transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
      style={{
        width: 'var(--help-w)',
        height: 'calc(100vh - 60px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-[18px] py-4 border-b border-[var(--border)] flex-shrink-0">
        <div className="flex-1 pr-2 min-w-0">
          <h3
            className="text-[13px] font-bold text-[var(--text)] leading-[1.3]"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {title}
          </h3>
          {hasHelp && dropdownValueCode && (
            <p className="text-[11px] font-medium text-[var(--accent)] leading-[1.3] mt-[2px] truncate">
              {dropdownValueCode}
            </p>
          )}
        </div>
        <button
          onClick={closeHelp}
          className={cn(
            'w-7 h-7 rounded-[6px] border border-[var(--border)]',
            'bg-transparent text-[var(--text-muted)] text-[14px]',
            'cursor-pointer flex items-center justify-center flex-shrink-0',
            'hover:bg-[var(--surface3)] hover:text-[var(--text)] transition-all duration-150'
          )}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-[18px] space-y-5">
        {loading ? (
          <div className="flex items-center justify-center gap-[8px] py-10 text-[var(--text-muted)]">
            <Loader2 size={18} strokeWidth={2} className="animate-spin" />
            <span className="text-[12px]">{getMessage('help_panel.loading')}</span>
          </div>
        ) : hasHelp ? (
          <>
            {blocks.map((block, i) => {
              if (block.type === 'paragraph') {
                const paragraphLines = block.text.split('\n')
                return (
                  <section key={i}>
                    {paragraphLines.length > 1 ? (
                      <ul className="space-y-[6px]">
                        {paragraphLines.map((line, j) => (
                          <li
                            key={j}
                            className={cn(
                              'flex gap-[8px] text-[12.5px] font-medium leading-[1.6] text-[var(--text-dim)]',
                              'pl-[10px] relative',
                              'before:content-["•"] before:absolute before:left-0',
                              'before:text-[var(--accent)] before:text-[14px] before:leading-[1.4]'
                            )}
                          >
                            {renderInlineMarkup(line)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[12.5px] font-medium leading-[1.7] text-[var(--text-dim)]">
                        {renderInlineMarkup(block.text)}
                      </p>
                    )}
                  </section>
                )
              }
              if (block.type === 'list') {
                return (
                  <section key={i}>
                    <div
                      className={cn(
                        'grid gap-x-3 gap-y-[6px] text-[12px] leading-[1.5]',
                        'p-[10px] rounded-[var(--radius-sm)] bg-[var(--surface2)] border border-[var(--border)]'
                      )}
                      style={{ gridTemplateColumns: 'max-content 1fr' }}
                    >
                      {block.items.map((item, j) => (
                        <Fragment key={j}>
                          <span className="font-semibold text-[var(--text)] whitespace-nowrap">{renderInlineMarkup(item.label)}</span>
                          <span className="text-[var(--text-dim)]">{renderInlineMarkup(item.value)}</span>
                        </Fragment>
                      ))}
                    </div>
                  </section>
                )
              }
              return (
                <section key={i} className="overflow-x-auto">
                  <table className="w-full text-[11px] leading-[1.4] border-collapse">
                    <tbody>
                      {block.rows.map((row, r) => (
                        <tr key={r}>
                          <td
                            className={cn(
                              'p-[6px] border border-[var(--border)] bg-[var(--surface2)]',
                              'font-semibold text-[var(--text)] align-top whitespace-normal'
                            )}
                          >
                            {renderInlineMarkup(row.label)}
                          </td>
                          {row.values.map((value, c) => (
                            <td
                              key={c}
                              className="p-[6px] border border-[var(--border)] text-[var(--text-dim)] text-center whitespace-nowrap"
                            >
                              {value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )
            })}

            {helpImages.length > 0 && (
              <section className={cn(helpImages.length > 1 && 'grid grid-cols-2 gap-[10px]')}>
                {helpImages.map((image, i) => (
                  <div key={i}>
                    <div
                      onClick={() => setLightboxIndex(i)}
                      className={cn(
                        'rounded-[var(--radius-sm)] bg-[var(--surface2)] border border-[var(--border)]',
                        'overflow-hidden cursor-pointer hover:border-[var(--accent)] hover:opacity-90 transition-all duration-150'
                      )}
                    >
                      <img
                        src={image.url}
                        alt={image.caption || title}
                        className={cn('w-full object-cover', helpImages.length > 1 ? 'max-h-[110px]' : 'max-h-[200px]')}
                      />
                    </div>
                    {image.caption && (
                      <p className="text-[10.5px] text-[var(--text-muted)] mt-1 leading-tight">{image.caption}</p>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* Footer tip */}
            <div
              className={cn(
                'flex gap-[10px] p-[12px] rounded-[var(--radius-sm)]',
                'bg-[rgba(61,142,240,0.05)] border border-[rgba(61,142,240,0.15)]'
              )}
            >
              <span className="text-[14px] flex-shrink-0">💡</span>
              <p className="text-[11px] leading-[1.6] text-[var(--text-dim)]">
                {getMessage('help_panel.close_hint')}
              </p>
            </div>
          </>
        ) : (
          <p className="text-[12px] text-[var(--text-muted)]">
            {getMessage('help_panel.empty_hint')}
          </p>
        )}
      </div>
      {/* Lightbox */}
      {lightboxIndex !== null && helpImages[lightboxIndex] && (
        <div
          className="absolute inset-0 z-[400] flex items-center justify-center bg-black/75 rounded-none"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="relative w-full px-6 flex flex-col items-center gap-3"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxIndex(null)}
              className={cn(
                'absolute -top-3 right-6 z-10',
                'w-7 h-7 rounded-full bg-[var(--surface)] border border-[var(--border)]',
                'text-[var(--text-muted)] text-[13px] flex items-center justify-center',
                'hover:bg-[var(--surface3)] hover:text-[var(--text)] transition-all duration-150 cursor-pointer'
              )}
            >
              ✕
            </button>
            {helpImages.length > 1 && (
              <button
                onClick={() => setLightboxIndex((lightboxIndex - 1 + helpImages.length) % helpImages.length)}
                aria-label="Previous image"
                className={cn(
                  'absolute left-6 top-1/2 -translate-y-1/2 z-10',
                  'w-7 h-7 rounded-full bg-[var(--surface)] border border-[var(--border)]',
                  'text-[var(--text-muted)] text-[13px] flex items-center justify-center',
                  'hover:bg-[var(--surface3)] hover:text-[var(--text)] transition-all duration-150 cursor-pointer'
                )}
              >
                ‹
              </button>
            )}
            {helpImages.length > 1 && (
              <button
                onClick={() => setLightboxIndex((lightboxIndex + 1) % helpImages.length)}
                aria-label="Next image"
                className={cn(
                  'absolute right-6 top-1/2 -translate-y-1/2 z-10',
                  'w-7 h-7 rounded-full bg-[var(--surface)] border border-[var(--border)]',
                  'text-[var(--text-muted)] text-[13px] flex items-center justify-center',
                  'hover:bg-[var(--surface3)] hover:text-[var(--text)] transition-all duration-150 cursor-pointer'
                )}
              >
                ›
              </button>
            )}
            <img
              src={helpImages[lightboxIndex].url}
              alt={helpImages[lightboxIndex].caption || title}
              className="w-full max-h-[60vh] rounded-[var(--radius-sm)] object-contain shadow-2xl"
            />
            {helpImages[lightboxIndex].caption && (
              <span className="text-[11px] text-white/70 leading-tight">{helpImages[lightboxIndex].caption}</span>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}
